import React from "react";
import { CreditCard, Puzzle, Percent, Headphones, Receipt } from "lucide-react";
import { formatBillingAmount } from "./_shared";
import { utcDay } from "../../lib/calendarDay";

/**
 * BillingBreakdownCard — the consolidated invoice: the 4 visible streams (C2).
 * Subscription · Add-ons · Platform fees (info) · Services · One-time purchases.
 *
 * `customPrice` is `billing/summary.custom_price`: when a negotiated price is in force the
 * subscription row (whose amount the server already states at that price) says so, with the
 * end date when there is one. `effective_until` is a calendar day (`utcDay`: written in the
 * reader's language `lang`, never sliding to the previous day west of Greenwich).
 */
export default function BillingBreakdownCard({ breakdown, customPrice = null, lang, t }) {
  if (!breakdown) return null;
  const cur = breakdown.currency;
  const cycleLabel = breakdown.billing_cycle === "annual" ? t("bill.perYear") : t("bill.perMonth");

  const untilText = utcDay(customPrice?.effective_until, lang);
  const cycleSub = breakdown.billing_cycle === "annual" ? t("bill.annualBilling") : t("bill.monthlyBilling");
  const subscriptionSub = breakdown.subscription.custom ? (
    <>
      {cycleSub}
      {" · "}
      <span data-testid="breakdown-custom-price" className="font-medium text-eh-sage-deep">
        {t("bill.customPrice.label")}
        {untilText && ` ${t("bill.customPrice.until", { date: untilText })}`}
      </span>
    </>
  ) : cycleSub;

  const Row = ({ icon: Icon, label, value, sub, info, valueTestId }) => (
    <div className="flex items-start justify-between py-3 border-b border-eh-border-soft last:border-0">
      <div className="flex items-start gap-3">
        <div className="w-8 h-8 rounded-lg bg-eh-cream flex items-center justify-center flex-shrink-0">
          <Icon className="w-4 h-4 text-eh-sage-deep" />
        </div>
        <div>
          <div className="text-sm font-medium text-eh-ink">{label}{info && <span className="ml-2 text-[10px] uppercase tracking-wide text-eh-muted">(info)</span>}</div>
          {sub && <div className="text-xs text-eh-muted mt-0.5">{sub}</div>}
        </div>
      </div>
      <div className="text-sm font-semibold text-eh-ink whitespace-nowrap" data-testid={valueTestId}>{value}</div>
    </div>
  );

  // Names from the locale layer (the wire `name` is the Stripe product name, a matching
  // key rather than copy). A `retired` line — its capability is not built — is still
  // billed until an operator removes it, so its amount stays in the total below, and its
  // name carries the marker so it never reads as a working feature.
  const addonsSub = breakdown.addons.length
    ? breakdown.addons.map((a, i) => (
      <React.Fragment key={a.key || i}>
        {i > 0 && ", "}
        <span data-testid={`breakdown-addon-${a.key}`}>
          {t(`bill.addonName.${a.key}`, null, a.key)}
          {a.retired && (
            <span
              data-testid={`breakdown-addon-${a.key}-retired`}
              className="ml-1 text-[10px] uppercase tracking-wide text-eh-muted"
            >
              {t("bill.addonRetiredShort")}
            </span>
          )}
        </span>
      </React.Fragment>
    ))
    : t("bill.none");
  const addonsTotal = breakdown.addons.reduce((s, a) => s + (a.amount || 0), 0);

  return (
    <div data-testid="billing-breakdown-card" className="bg-white rounded-2xl border border-eh-border-soft p-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="font-display text-xl text-eh-ink">{t("bill.consolidated")}</h2>
        <span className="text-xs text-eh-muted">{t("bill.fourStreams")}</span>
      </div>

      <Row
        icon={CreditCard}
        label={t("bill.subscriptionLabel", { name: breakdown.subscription.name || breakdown.subscription.plan_id || "" })}
        sub={subscriptionSub}
        value={`${formatBillingAmount(breakdown.subscription.amount, cur)}${cycleLabel}`}
      />
      <Row
        icon={Puzzle}
        label={t("bill.addons")}
        sub={addonsSub}
        value={`${formatBillingAmount(addonsTotal, cur)}${t("bill.perMonth")}`}
        valueTestId="breakdown-addons-total"
      />
      <Row
        icon={Percent}
        label={t("bill.platformFees")}
        info
        sub={t("bill.platformFeesSub")}
        value={formatBillingAmount(breakdown.platform_fees_collected, cur)}
      />
      <Row
        icon={Headphones}
        label={t("bill.solunaryServices")}
        sub={breakdown.services.length ? breakdown.services.map((s) => s.tier).join(", ") : t("bill.none")}
        value={breakdown.services.length ? t("bill.interestsCount", { count: breakdown.services.length }) : "—"}
      />
      <Row
        icon={Receipt}
        label={t("bill.oneTimeRecent")}
        sub={breakdown.one_time.length ? breakdown.one_time.map((o) => o.label).slice(0, 3).join(", ") : t("bill.none")}
        value={breakdown.one_time.length ? formatBillingAmount(breakdown.one_time.reduce((s, o) => s + (o.amount || 0), 0), cur) : "—"}
      />

      <div className="flex items-center justify-between mt-4 pt-4 border-t-2 border-eh-ink/10">
        <span className="text-sm font-medium text-eh-ink">{t("bill.recurringTotal")}</span>
        <span className="font-sol font-bold text-lg text-eh-ink" data-testid="billing-recurring-total">
          {formatBillingAmount(breakdown.recurring_total, cur)}{t("bill.perMonth")}
        </span>
      </div>
    </div>
  );
}
